'use client';

import { MoreVertical } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { cn } from '@/shared/lib/cn';
import { usePageActionsStore } from '@/shared/stores/page-actions-store';
import { FeedbackMenuItem } from '@/features/support';

interface ProjectActionsMenuProps {
  variant?: 'hero' | 'default';
}

export function ProjectActionsMenu({
  variant = 'default',
}: ProjectActionsMenuProps) {
  const menuItems = usePageActionsStore((state) => state.menuItems);
  const t = useTranslations('common');

  if (menuItems.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            // Hero: dark glass circle over the light teal wash (mirrors the
            // Apple toolbar's glass ellipsis button).
            variant === 'hero' &&
              'rounded-full bg-background/60 backdrop-blur-sm text-foreground hover:bg-background/80'
          )}
          aria-label={t('moreActions')}
        >
          <MoreVertical className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {menuItems.map((item, index) => (
          <DropdownMenuItem
            key={index}
            onClick={item.onClick}
            className={cn(
              item.variant === 'destructive' && 'text-destructive',
              item.disabled && 'opacity-50 cursor-not-allowed'
            )}
            disabled={item.disabled}
          >
            {item.label}
          </DropdownMenuItem>
        ))}
        {/* Phone-only twin of the header's Feedback button — see
            `FeedbackHeaderButton`, which is hidden below `md`. Brings its own
            separator. */}
        <FeedbackMenuItem />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
